import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://breedling.com";
const DEFAULT_TITLE = "Breedling | Premium Breeder Marketplace";
const DEFAULT_DESCRIPTION =
  "Breedling connects buyers with verified breeders, service providers, shelters, auctions, and quality animal listings.";

const ROUTE_META = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/browse": {
    title: "Browse Animal Listings | Breedling",
    description: "Browse verified animal listings across equine, canine, feline, cattle, exotic, and farm categories.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Breedling",
    description: "Learn how Breedling collects, uses, and protects information across its marketplace.",
  },
  "/terms-of-service": {
    title: "Terms of Service | Breedling",
    description: "Review the terms governing Breedling marketplace accounts, listings, auctions, and transactions.",
  },
  "/studs": {
    title: "Stud Services | Breedling",
    description: "Find verified stud services and breeding programs on Breedling.",
  },
  "/mares": {
    title: "Mare Services | Breedling",
    description: "Find mare services and breeding partnerships on Breedling.",
  },
  "/litters": {
    title: "Available Litters | Breedling",
    description: "Discover available litters from breeders on Breedling.",
  },
  "/adoptions": {
    title: "Animal Adoptions | Breedling",
    description: "Browse animal adoption opportunities from shelters and rescues on Breedling.",
  },
  "/auctions": {
    title: "Animal Auctions | Breedling",
    description: "Browse live and upcoming animal auctions on Breedling.",
  },
  "/transporters": {
    title: "Animal Transporters | Breedling",
    description: "Find animal transport providers in the Breedling network.",
  },
  "/vendors": {
    title: "Animal Service Providers | Breedling",
    description: "Find animal-related vendors and service providers on Breedling.",
  },
  "/shelters": {
    title: "Shelters and Rescues | Breedling",
    description: "Discover shelters, rescues, and adoption opportunities on Breedling.",
  },
};

const KNOWN_ROUTE_PATHS = [
  /^\/$/,
  /^\/admin(?:\/dashboard)?$/,
  /^\/auth\/callback$/,
  /^\/browse(?:\/[^/]+(?:\/(?:services|breeding)(?:\/(?:find|register))?)?)?$/,
  /^\/listing\/[^/]+$/,
  /^\/search$/,
  /^\/dashboard(?:\/(?:breeder(?:\/new-listing)?|shelter|transporter|vendor))?$/,
  /^\/subscription-plans$/,
  /^\/onboarding\/breeder$/,
  /^\/messages(?:\/[^/]+)?$/,
  /^\/(?:studs|mares|farm-breeding|litters|adoptions)$/,
  /^\/shelter\/register$/,
  /^\/transporters(?:\/[^/]+)?$/,
  /^\/transporter\/register$/,
  /^\/auctions(?:\/[^/]+)?$/,
  /^\/my\/auctions$/,
  /^\/vendors$/,
  /^\/vendor\/register$/,
  /^\/(?:privacy-policy|terms-of-service|developers|shelters|signin)$/,
];

const NOINDEX_PATHS = [
  /^\/admin(?:\/|$)/,
  /^\/auth(?:\/|$)/,
  /^\/dashboard(?:\/|$)/,
  /^\/messages(?:\/|$)/,
  /^\/my(?:\/|$)/,
  /^\/onboarding(?:\/|$)/,
  /^\/search(?:\/|$)/,
  /^\/signin(?:\/|$)/,
  /^\/shelter\/register(?:\/|$)/,
  /^\/transporter\/register(?:\/|$)/,
  /^\/vendor\/register(?:\/|$)/,
  /^\/browse\/[^/]+\/(?:services|breeding)\/register(?:\/|$)/,
];

const upsertMeta = (name, content) => {
  let element = document.head.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

const Seo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath = pathname !== "/" ? pathname.replace(/\/$/, "") : "/";
    const isKnownRoute = KNOWN_ROUTE_PATHS.some((pattern) => pattern.test(normalizedPath));
    const isNoindex =
      !isKnownRoute || NOINDEX_PATHS.some((pattern) => pattern.test(normalizedPath));
    const isBrowseSpecies = /^\/browse\/[^/]+$/.test(normalizedPath);
    const species = isBrowseSpecies
      ? normalizedPath.split("/").pop().replace(/-/g, " ")
      : null;
    const meta = ROUTE_META[normalizedPath] || (species
      ? {
          title: `${species.charAt(0).toUpperCase() + species.slice(1)} Listings | Breedling`,
          description: `Browse ${species} listings from breeders and sellers on Breedling.`,
        }
      : {
          title: DEFAULT_TITLE,
          description: DEFAULT_DESCRIPTION,
        });

    document.title = meta.title;
    upsertMeta("description", meta.description);
    upsertMeta("robots", isNoindex ? "noindex, follow" : "index, follow");
    upsertCanonical(`${SITE_URL}${normalizedPath === "/" ? "/" : normalizedPath}`);
  }, [pathname]);

  return null;
};

export default Seo;
